// Tooltips.js
import React, { cloneElement, useState } from "react";
import { useFloating, offset, flip, shift, arrow, autoUpdate } from "@floating-ui/react";
import classNames from "classnames";
import Portal from "../../Components/layout/Portal";

const Tooltips = ({
  children,
  title,
  placement = "top",
  delay = 0,
  className,
  isDisplayInline,
  isDisableElements,
}) => {
  const [open, setOpen] = useState(false);
  const [arrowEl, setArrowEl] = useState(null);

  const { refs, floatingStyles, middlewareData, placement: finalPlacement } = useFloating({
    placement,
    middleware: [offset(8), flip(), shift(), arrow({ element: arrowEl })],
    whileElementsMounted: autoUpdate,
  });

  const onEnter = () => {
    setOpen(true);
    if (children?.props?.onMouseEnter) children.props.onMouseEnter();
  };
  const onLeave = () => {
    setTimeout(() => setOpen(false), delay);
    if (children?.props?.onMouseLeave) children.props.onMouseLeave();
  };

  const PROPS = {
    className: classNames({ "d-inline-block": isDisplayInline }, children?.props?.className),
    onMouseEnter: onEnter,
    onMouseLeave: onLeave,
    ref: refs.setReference, // React 19 safe
  };

  // Arrow sizing / offset
  const arrowSize = 6; // px (square that is rotated 45deg)
  const half = Math.round(arrowSize / 2);

  // Determine which side is static (where to offset the arrow outside the box)
  const side = (finalPlacement || placement).split("-")[0];

  // Build arrow style based on placement and middleware-provided x/y
  const arrowStyles = {
    position: "absolute",
    width: `${arrowSize}px`,
    height: `${arrowSize}px`,
    background: "inherit", // match tooltip background
    transform: "rotate(45deg)",
    pointerEvents: "none",
  };

  if (side === "top" || side === "bottom") {
    // horizontal positioning
    arrowStyles.left =
      middlewareData.arrow?.x != null ? `${middlewareData.arrow.x}px` : `calc(50% - ${half}px)`;
    arrowStyles[side === "top" ? "bottom" : "top"] = `-${half}px`;
  } else {
    // left or right
    arrowStyles.top =
      middlewareData.arrow?.y != null ? `${middlewareData.arrow.y}px` : `calc(50% - ${half}px)`;
    arrowStyles[side === "left" ? "right" : "left"] = `-${half}px`;
  }

  return (
    <>
      {cloneElement(
        typeof children === "string" ? <span {...PROPS}>{children}</span> : (isDisableElements ? <span className="d-inline-block" tabIndex={0}>{children}</span> : children),
        PROPS
      )}

      {open && (
        <Portal>
          <div
            ref={refs.setFloating}
            role="tooltip"
            className={classNames("floating-tooltip", className)}
            style={{
              ...floatingStyles,
              position: "absolute",
              background: "#111",        // choose the color you want
              color: "#fff",
              padding: "6px 10px",
              borderRadius: 20,
              fontSize: 11,
              zIndex: 9999,
              boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
              overflow: "visible",       // <--- important so arrow isn't clipped
            }}
            data-placement={finalPlacement}
          >
            {/* Arrow element (rotated square) */}
            <div ref={setArrowEl} style={arrowStyles} />

            <div className="tooltip-inner">{title}</div>
          </div>
        </Portal>
      )}
    </>
  );
};

export default Tooltips;
